import styled from 'styled-components';
import { motion } from 'framer-motion';
import DesignTokens from '../styles/DesignTokens';
import GlassmorphismTokens from '../styles/GlassmorphismTokens';
import { optimizeBackgroundImage } from '../components/common/OptimizedBackgroundImage';

// All styled components for ItineraryPage
const PageContainer = styled.div`
  padding-top: ${props => props.$noNavbar ? '0' : '0'};
  background: #000c1a;
  min-height: ${props => props.$noNavbar ? '0' : '100vh'};
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: ${props => props.$noNavbar ? 'hidden' : 'auto'};
  width: 100%;
  max-width: 100vw;
  ${props => props.$noNavbar && 'margin-bottom: -80px;'}

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    padding-top: ${props => props.$noNavbar ? '0' : '70px'};
    padding-bottom: 0;
  }
`;

// Scenic background wrapper for the form page
const ScenicFormBackground = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${DesignTokens.spacing.xl} ${DesignTokens.spacing.lg};
  padding-top: calc(${DesignTokens.spacing.xl} + 60px);

  @media (min-width: ${props => props.theme.breakpoints?.desktop || '1024px'}) {
    min-height: calc(100vh - 70px);
    padding-top: ${DesignTokens.spacing.xl};
  }
  background-image: url(${props => props.$bgImage || GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.scenicOverlay};
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

// Hero components removed - reverting to original layout

// Two-column layout container (not used - removed sidebar)
/* eslint-disable no-unused-vars */
const LayoutContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: calc(100vh - 70px);
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

// Left Sidebar - AI Chat Interface (not used - removed)
const Sidebar = styled.div`
  width: 380px;
  background: ${props => props.theme.colors.surface || '#FAFAFA'};
  border-right: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  display: flex;
  flex-direction: column;
  padding: ${DesignTokens.spacing.xl};
  overflow-y: auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
    max-height: 400px;
  }
`;

const CompassIconContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary || '#4285F4'} 0%, ${props => props.theme.colors.secondary || '#34A853'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${DesignTokens.spacing.xl};
  box-shadow: ${DesignTokens.shadow.md};

  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.md};
  margin-bottom: ${DesignTokens.spacing.lg};
`;

const AIPromptBubble = styled.div`
  background: ${props => props.theme.colors.primaryLight || '#E8F0FE'};
  border-radius: ${DesignTokens.borderRadius.lg};
  padding: ${DesignTokens.spacing.md};
  color: ${props => props.theme.colors.text || '#202124'};
  font-size: ${DesignTokens.typography.fontSize.base};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  border: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
`;

const UserBubble = styled.div`
  background: ${props => props.theme.colors.primary || '#4285F4'};
  color: white;
  border-radius: ${DesignTokens.borderRadius.lg};
  padding: ${DesignTokens.spacing.md};
  font-size: ${DesignTokens.typography.fontSize.base};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  align-self: flex-end;
  max-width: 85%;
`;

const VoiceControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.md};
  padding-top: ${DesignTokens.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
`;

const MicrophoneButton = styled(motion.button)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary || '#4285F4'};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${DesignTokens.shadow.md};

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: ${props => props.theme.colors.primaryHover || '#3367D6'};
    transform: scale(1.05);
  }
`;

const VolumeSlider = styled.input`
  flex: 1;
  height: 4px;
  border-radius: ${DesignTokens.borderRadius.full};
  background: ${props => props.theme.colors.borderLight || '#E8EAED'};
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary || '#4285F4'};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary || '#4285F4'};
    cursor: pointer;
    border: none;
  }
`;

// Main Content Area
const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: ${props => props.theme.colors.background || '#FFFFFF'};
`;

const MainHeader = styled.div`
  padding: ${DesignTokens.spacing.xl} ${DesignTokens.spacing['2xl']};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.lg};
    flex-direction: column;
    gap: ${DesignTokens.spacing.md};
    align-items: flex-start;
  }
`;

const MainTitle = styled.h1`
  font-size: ${DesignTokens.typography.fontSize['3xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const SearchIconButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.surface || '#FAFAFA'};
  border: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${DesignTokens.transition.normal};

  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  }

  &:hover {
    background: ${props => props.theme.colors.primaryLight || '#E8F0FE'};
    border-color: ${props => props.theme.colors.primary || '#4285F4'};
    
    svg {
      color: ${props => props.theme.colors.primary || '#4285F4'};
    }
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: ${DesignTokens.spacing['2xl']};
  overflow-y: auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.lg};
  }
`;
/* eslint-enable no-unused-vars */

const SectionTitle = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  margin: 0 0 ${DesignTokens.spacing.xl} 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.xl};
    margin: 0 0 ${DesignTokens.spacing.md} 0;
    
    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 1rem;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 1rem 0.75rem;
  }
`;

const Title = styled.h1`
  font-size: ${DesignTokens.typography.fontSize['3xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize['2xl']};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.md};
  margin-bottom: ${DesignTokens.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: ${DesignTokens.spacing.sm};
    margin-bottom: ${DesignTokens.spacing.lg};
    flex-wrap: wrap;
  }
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  background: white;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${DesignTokens.borderRadius.md};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: ${DesignTokens.typography.fontSize.base};
  transition: all ${DesignTokens.transition.normal};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
    font-size: ${DesignTokens.typography.fontSize.sm};
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// Modern Form Card - Glass-morphism Design
const FormContainer = styled.div`
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: 32px;
  padding: 2rem;
  box-shadow: ${GlassmorphismTokens.shadow.glass};
  position: relative;
  overflow: visible;
  width: 100%;
  max-width: 720px;
  margin: 0 auto 2rem auto;
  color: white;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.88);
  }
`;

const ExpandedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const ExpandedTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 24px;
    height: 24px;
    color: #FF385C;
  }
`;

// Primary Fields Row (Destination + Days)
const PrimaryFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  position: relative;
`;

const FieldLabelModern = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #717171;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const FieldInputModern = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid #ebebeb;
  border-radius: 12px;
  font-size: 1rem;
  color: #222;
  background: #fff;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #222;
    box-shadow: 0 0 0 1px #222;
  }

  &::placeholder {
    color: #b0b0b0;
  }
`;

const FieldSelectModern = styled.select`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid #ebebeb;
  border-radius: 12px;
  font-size: 1rem;
  color: #222;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23717171' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: #222;
    box-shadow: 0 0 0 1px #222;
  }
`;

// Expandable Options Section
const OptionsToggle = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0;
  background: none;
  border: none;
  color: #717171;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  justify-content: flex-start;
  transition: color 0.2s ease;

  &:hover {
    color: #222;
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s ease;
    transform: rotate(${props => props.isOpen ? '90deg' : '0deg'});
  }
`;

const OptionsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Generate Button - Blue Gradient CTA
const GenerateButton = styled(motion.button)`
  width: 100%;
  padding: 1.125rem 2rem;
  background: ${props => props.disabled
    ? '#555'
    : GlassmorphismTokens.gradient.blueCTA};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.0625rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 1rem 1.25rem;
  }
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  box-shadow: ${GlassmorphismTokens.shadow.blueCTA};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.blueCTAHover};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${GlassmorphismTokens.shadow.blueCTAHover};

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  span, svg {
    position: relative;
    z-index: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Itinerary Display
// Legacy components - not used in new design (kept for reference)
/* eslint-disable no-unused-vars */
const ItineraryContainer = styled(motion.div)`
  background: white;
  border-radius: ${DesignTokens.borderRadius.lg};
  padding: ${DesignTokens.spacing.xl};
  box-shadow: ${DesignTokens.shadow.md};
`;

const ItineraryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${DesignTokens.spacing.xl};
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.md};
`;

const ItineraryTitle = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.md};
  flex-wrap: wrap;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  background: ${props => props.variant === 'primary' 
    ? props.theme.colors.tertiary || '#EA4335'
    : 'white'};
  color: ${props => props.variant === 'primary' 
    ? 'white'
    : props.theme.colors.text};
  border: 2px solid ${props => props.variant === 'primary'
    ? props.theme.colors.tertiary || '#EA4335'
    : props.theme.colors.border};
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${DesignTokens.transition.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${DesignTokens.shadow.md};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TripSummary = styled.div`
  background: ${props => props.theme.colors.accentLight || '#FEF7E0'};
  padding: ${DesignTokens.spacing.lg};
  border-radius: ${DesignTokens.borderRadius.md};
  margin-bottom: ${DesignTokens.spacing.xl};
  border-left: 4px solid ${props => props.theme.colors.accent || '#FBBC04'};
`;

const TripSummaryText = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

// Day Cards Grid (Screenshot Style)
const DayCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${DesignTokens.spacing.xl};
  margin-bottom: ${DesignTokens.spacing['2xl']};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${DesignTokens.spacing.lg};
  }
`;

// Day Card (Gold Standard - Compact Card Design)
const DayCard = styled(motion.div)`
  background: white;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${DesignTokens.shadow.md};
  border: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  transition: all ${DesignTokens.transition.normal};
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: ${DesignTokens.shadow.lg};
    transform: translateY(-2px);
  }
`;

// Day Header Banner (colored based on day index)
const DayHeaderBanner = styled.div`
  background: ${props => {
    const dayIndex = props.dayIndex || 0;
    if (dayIndex % 3 === 0) return props.theme.colors.accentLight || '#FEF7E0';
    if (dayIndex % 3 === 1) return props.theme.colors.secondaryLight || '#E6F4EA';
    return props.theme.colors.primaryLight || '#E8F0FE';
  }};
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
`;

const DayHeaderIcon = styled.span`
  font-size: ${DesignTokens.typography.fontSize.lg};
`;

const DayHeaderTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0;
`;

const DayCardContent = styled.div`
  padding: ${DesignTokens.spacing.lg};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.lg};
`;

const DayActivities = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.lg};
`;

const ActivityTimeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.md};
`;

const ActivityTimeLabel = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  text-transform: uppercase;
  letter-spacing: ${DesignTokens.typography.letterSpacing.wide};
  margin-bottom: ${DesignTokens.spacing.xs};
`;

const ActivityText = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0;
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
`;

// Compact Activity Item (Gold Standard Design)
const ActivityItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.sm};
  padding-bottom: ${DesignTokens.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ActivityTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${DesignTokens.spacing.md};
`;

const ActivityTitle = styled.div`
  flex: 1;
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text || '#202124'};
  line-height: ${DesignTokens.typography.lineHeight.normal};
`;

const ActivityDuration = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActivityGlimpse = styled.div`
  margin-top: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.sm};
  background: ${props => props.theme.colors.surface || '#FAFAFA'};
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
`;

const ActivityNextUp = styled.div`
  margin-top: ${DesignTokens.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  
  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.primary || '#4285F4'};
  }
`;

// Activity Meta Row (duration, cost, transit)
const ActivityMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  margin-top: ${DesignTokens.spacing.xs};
`;

// Cost Badge (Airbnb style)
const CostBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: ${props => {
    const cost = props.cost || '$';
    if (cost === '$$$') return '#FEE2E2';
    if (cost === '$$') return '#FEF3C7';
    return '#D1FAE5';
  }};
  color: ${props => {
    const cost = props.cost || '$';
    if (cost === '$$$') return '#991B1B';
    if (cost === '$$') return '#92400E';
    return '#065F46';
  }};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
`;

// Transit Time Badge
const TransitBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: ${props => props.theme.colors.surface || '#F3F4F6'};
`;

const TransitDetailsSection = styled.div`
  margin-top: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  background: linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%);
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: #0369A1;
  line-height: 1.5;
  border-left: 3px solid #0EA5E9;
`;

// Why Fun Section (highlighted)
const WhyFunSection = styled.div`
  margin-top: ${DesignTokens.spacing.sm};
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: #92400E;
  display: flex;
  align-items: flex-start;
  gap: ${DesignTokens.spacing.sm};
  
  &::before {
    content: '✨';
    flex-shrink: 0;
  }
`;

// Source Attribution
const SourceAttribution = styled.div`
  margin-top: ${DesignTokens.spacing.xs};
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: ${props => props.theme.colors.textTertiary || '#9CA3AF'};
  font-style: italic;
`;

// Daily Notes Card
const DailyNotesCard = styled.div`
  margin-top: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.md};
  background: linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%);
  border-radius: ${DesignTokens.borderRadius.md};
  border-left: 4px solid #8B5CF6;
  
  h4 {
    font-size: ${DesignTokens.typography.fontSize.sm};
    font-weight: ${DesignTokens.typography.fontWeight.semibold};
    color: #5B21B6;
    margin: 0 0 ${DesignTokens.spacing.xs} 0;
    display: flex;
    align-items: center;
    gap: ${DesignTokens.spacing.xs};
  }
  
  p {
    font-size: ${DesignTokens.typography.fontSize.sm};
    color: #6D28D9;
    margin: 0;
    line-height: ${DesignTokens.typography.lineHeight.relaxed};
  }
`;

// Day Tip Banner (at top of day card)
const DayTipBanner = styled.div`
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  background: linear-gradient(90deg, #ECFDF5 0%, #D1FAE5 100%);
  border-bottom: 1px solid #A7F3D0;
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: #065F46;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  
  &::before {
    content: '💡';
  }
`;

// Tips Section (at bottom)
const TipsSection = styled.div`
  margin-top: ${DesignTokens.spacing.xl};
  padding: ${DesignTokens.spacing.lg};
  background: linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%);
  border-radius: ${DesignTokens.borderRadius.lg};
  border: 1px solid #BBF7D0;
`;

const TipsSectionTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.h3};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.heading};
  color: #166534;
  margin: 0 0 ${DesignTokens.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const TipsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.sm};
`;

const TipItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${DesignTokens.spacing.sm};
  font-size: ${DesignTokens.typography.fontSize.body};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: #15803D;

  &::before {
    content: '✓';
    color: #22C55E;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

// Special Recommendations Section
const SpecialRecommendationsSection = styled.div`
  margin-top: ${DesignTokens.spacing.xl};
  padding: ${DesignTokens.spacing.xl} 0;
`;

const SpecialRecommendationsTitle = styled.h2`
  font-size: ${DesignTokens.typography.fontSize.h2};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  line-height: ${DesignTokens.typography.lineHeight.heading};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const SpecialRecommendationsSubtitle = styled.p`
  font-size: ${DesignTokens.typography.fontSize.body};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  margin: 0 0 ${DesignTokens.spacing.lg} 0;
`;

const RecommendationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${DesignTokens.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const RecommendationCard = styled(motion.div)`
  background: white;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${DesignTokens.shadow.md};
  border: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  transition: all ${DesignTokens.transition.normal};
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: ${DesignTokens.shadow.lg};
    transform: translateY(-2px);
  }
`;

const RecommendationHeader = styled.div`
  background: linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%);
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${DesignTokens.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
`;

const RecommendationName = styled.h4`
  font-size: ${DesignTokens.typography.fontSize.h4};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.heading};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0;
  flex: 1;
`;

const RecommendationPlaceType = styled.span`
  font-size: ${DesignTokens.typography.fontSize.caption};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  color: #7C3AED;
  background: white;
  padding: 4px 12px;
  border-radius: ${DesignTokens.borderRadius.full};
  text-transform: capitalize;
  white-space: nowrap;
`;

const RecommendationContent = styled.div`
  padding: ${DesignTokens.spacing.lg};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.md};
`;

const RecommendationWhyFamous = styled.p`
  font-size: ${DesignTokens.typography.fontSize.body};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0;
`;

const RecommendationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.sm};
  border-top: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  padding-top: ${DesignTokens.spacing.md};
`;

const RecommendationDetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${DesignTokens.spacing.sm};
  font-size: ${DesignTokens.typography.fontSize.bodySecondary};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
`;

const RecommendationDetailLabel = styled.span`
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text || '#202124'};
  min-width: 100px;
  flex-shrink: 0;
`;

const RecommendationDetailValue = styled.span`
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
`;

const RecommendationInsiderTip = styled.div`
  background: linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%);
  border-left: 3px solid #8B5CF6;
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  border-radius: ${DesignTokens.borderRadius.sm};
  font-size: ${DesignTokens.typography.fontSize.bodySecondary};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: #6D28D9;

  &::before {
    content: '💡 ';
    font-style: normal;
  }
`;

// Accommodation Section
const AccommodationSection = styled.div`
  margin-top: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.md};
  background: ${props => props.theme.colors.primaryLight || '#E8F0FE'};
  border-radius: ${DesignTokens.borderRadius.md};
  border-left: 3px solid ${props => props.theme.colors.primary || '#4285F4'};
`;

const AccommodationTitle = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text || '#202124'};
  margin-bottom: ${DesignTokens.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  
  &::before {
    content: '🏨';
    font-size: ${DesignTokens.typography.fontSize.base};
  }
`;

const AccommodationText = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.text || '#202124'};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  margin-bottom: ${DesignTokens.spacing.sm};
`;

const AccommodationLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  color: ${props => props.theme.colors.primary || '#4285F4'};
  text-decoration: none;
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: white;
  border-radius: ${DesignTokens.borderRadius.sm};
  border: 1px solid ${props => props.theme.colors.primary || '#4285F4'};
  transition: all ${DesignTokens.transition.normal};
  
  &:hover {
    background: ${props => props.theme.colors.primary || '#4285F4'};
    color: white;
    transform: translateY(-1px);
    box-shadow: ${DesignTokens.shadow.sm};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Browse All Button
const BrowseAllButton = styled(motion.button)`
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.xl};
  background: linear-gradient(135deg, #D797FB 0%, #8B5CF6 100%);
  color: white;
  border: none;
  border-radius: ${DesignTokens.borderRadius.lg};
  cursor: pointer;
  font-size: ${DesignTokens.typography.fontSize.md};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(215, 151, 251, 0.3);
  transition: all ${DesignTokens.transition.normal};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(215, 151, 251, 0.4);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.lg};
    font-size: ${DesignTokens.typography.fontSize.sm};
    width: 100%;
    max-width: 300px;
    
    svg {
      width: 16px !important;
      height: 16px !important;
    }
  }
`;

const ToggleViewButton = styled(motion.button)`
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.md};
  background: ${props => props.theme.colors?.primaryLight || '#E8F0FE'};
  color: ${props => props.theme.colors?.primary || '#4285F4'};
  border: none;
  border-radius: ${DesignTokens.borderRadius.md};
  cursor: pointer;
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  white-space: nowrap;
  transition: all ${DesignTokens.transition.normal};
  
  &:hover {
    background: ${props => props.theme.colors?.primary || '#4285F4'};
    color: white;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
    font-size: ${DesignTokens.typography.fontSize.xs};
  }
`;

// Available Itineraries Section (matching Featured Itineraries style)
const AvailableItinerariesContainer = styled.div`
  margin-bottom: ${DesignTokens.spacing['2xl']};
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-bottom: ${DesignTokens.spacing.xl};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${DesignTokens.spacing.lg};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-bottom: ${DesignTokens.spacing.md};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  max-width: 100%;
  word-wrap: break-word;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.sm};
  }
`;

// Itinerary Type Badge
const ItineraryTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: linear-gradient(42deg, rgb(222 232 196) 0%, rgb(215 147 251) 100%);
  color: white;
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  text-transform: capitalize;
  white-space: nowrap;
  margin-left: ${DesignTokens.spacing.sm};
`;

const ItineraryCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${DesignTokens.spacing.xl};
  margin-top: ${DesignTokens.spacing.lg};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${DesignTokens.spacing.md};
    grid-template-columns: minmax(0, 1fr);
  }
`;

// Carousel Components for Itineraries
const ItineraryCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  padding: 0 60px; /* Space for carousel buttons on desktop */
  margin-top: ${DesignTokens.spacing.lg};
  overflow: hidden;
  box-sizing: border-box;
  
  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    padding: 0 40px; /* Reduced padding for mobile */
  }
`;

const ItineraryCarouselCardsContainer = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.lg};
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: ${DesignTokens.spacing.sm};
  scroll-behavior: smooth;
  position: relative;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.colors.border} transparent;
  max-width: 100%;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    gap: ${DesignTokens.spacing.md};
    
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
`;

const ItineraryCarouselCard = styled(motion.div)`
  flex-shrink: 0;
  width: 320px;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  background: white;
  box-shadow: ${DesignTokens.shadow.md};
  transition: all ${DesignTokens.transition.normal};
  cursor: pointer;
  text-decoration: none;
  display: block;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${DesignTokens.shadow.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 280px;
  }
`;

const CarouselButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  box-shadow: ${DesignTokens.shadow.md};
  opacity: 0.8;
  transition: opacity ${DesignTokens.transition.normal}, transform ${DesignTokens.transition.normal};
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    background: white;
    transform: translateY(-50%) scale(1.05);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.colors.text};
    flex-shrink: 0;
    pointer-events: none;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const CarouselPrevButton = styled(CarouselButton)`
  left: 0;
  z-index: 20;
  opacity: ${props => props.disabled ? 0.3 : 0.9};
  
  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    left: 5px;
  }
`;

const CarouselNextButton = styled(CarouselButton)`
  right: 0;
  z-index: 20;
  opacity: ${props => props.disabled ? 0.3 : 0.9};
  
  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    right: 5px;
  }
`;

// Country Section Header with Carousel
const CountryCard = styled(motion.div)`
  flex-shrink: 0;
  width: 280px;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  background: white;
  box-shadow: ${DesignTokens.shadow.md};
  cursor: pointer;
  transition: all ${DesignTokens.transition.normal};
  text-decoration: none;
  display: block;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${DesignTokens.shadow.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 220px;
    min-width: 220px;
  }
`;

const CountrySection = styled.div`
  margin-bottom: ${DesignTokens.spacing['2xl']};
`;

const CountrySectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${DesignTokens.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${DesignTokens.spacing.md};
  }
`;

const CountryTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const CountryCount = styled.span`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${DesignTokens.typography.fontWeight.normal};
`;

const ItineraryCard = styled(motion.div)`
  display: block;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  background: white;
  box-shadow: ${DesignTokens.shadow.md};
  text-decoration: none;
  transition: all ${DesignTokens.transition.normal};
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${DesignTokens.shadow.lg};
  }
`;

const ItineraryImage = styled.div`
  width: 100%;
  aspect-ratio: ${DesignTokens.aspectRatio.wide};
  background-image: url(${props => optimizeBackgroundImage(props.imageUrl || 'https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b', { width: 800, height: 450, quality: 85 })});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const AIBadgeSmall = styled.span`
  position: absolute;
  top: ${DesignTokens.spacing.sm};
  right: ${DesignTokens.spacing.sm};
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: linear-gradient(42deg, rgb(222 232 196) 0%, rgb(215 147 251) 100%);
  color: white;
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
`;

const ItineraryContent = styled.div`
  padding: ${DesignTokens.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.md};
  }
`;

const ItineraryCardTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${DesignTokens.spacing.xs} 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.lg};
  }
`;

const ItineraryCardDescription = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${DesignTokens.spacing.xs} 0;
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.xs};
    line-height: ${DesignTokens.typography.lineHeight.normal};
  }
`;

const ItineraryCardMeta = styled.p`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: ${props => props.theme.colors.textTertiary};
  margin: 0;
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.xs};
`;

const FavoriteButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${DesignTokens.spacing.xs};
  border-radius: ${DesignTokens.borderRadius.md};
  transition: all ${DesignTokens.transition.normal};
  color: ${props => props.isFavorited ? '#EF4444' : props.theme.colors?.textTertiary || '#666'};
  
  &:hover {
    background: ${props => props.theme.colors?.surface || '#F8F9FA'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// Budget Tracker Section
const BudgetTrackerSection = styled.div`
  background: white;
  border-radius: ${DesignTokens.borderRadius.lg};
  padding: ${DesignTokens.spacing.xl};
  box-shadow: ${DesignTokens.shadow.md};
  border: 1px solid ${props => props.theme.colors.borderLight || '#E8EAED'};
  margin-bottom: ${DesignTokens.spacing.xl};
`;

const BudgetTrackerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${DesignTokens.spacing.lg};
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BudgetTrackerTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text || '#202124'};
  margin: 0;
`;

const EstimatedCost = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.md};
  flex-wrap: wrap;
`;

const CostAmount = styled.div`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary || '#4285F4'};
`;

const BudgetNoteInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${DesignTokens.spacing.md};
  border: 2px solid ${props => props.theme.colors.border || '#DADCE0'};
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.base};
  font-family: ${DesignTokens.typography.fontFamily.primary};
  resize: vertical;
  transition: all ${DesignTokens.transition.normal};
  margin-bottom: ${DesignTokens.spacing.md};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary || '#4285F4'};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight || '#E8F0FE'};
  }
`;

const CostSlider = styled.input`
  width: 100%;
  height: 8px;
  border-radius: ${DesignTokens.borderRadius.full};
  background: ${props => props.theme.colors.borderLight || '#E8EAED'};
  outline: none;
  -webkit-appearance: none;
  margin-top: ${DesignTokens.spacing.md};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary || '#4285F4'};
    cursor: pointer;
    box-shadow: ${DesignTokens.shadow.md};
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary || '#4285F4'};
    cursor: pointer;
    border: none;
    box-shadow: ${DesignTokens.shadow.md};
  }
`;

// Share & Collaborate Button (not used - using ActionButton instead)
const ShareButton = styled(motion.button)`
  position: fixed;
  bottom: ${DesignTokens.spacing.xl};
  right: ${DesignTokens.spacing.xl};
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.xl};
  background: ${props => props.theme.colors.primary || '#4285F4'};
  color: white;
  border: none;
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  box-shadow: ${DesignTokens.shadow.lg};
  z-index: 100;
  transition: all ${DesignTokens.transition.normal};

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${props => props.theme.colors.primaryHover || '#3367D6'};
    transform: translateY(-2px);
    box-shadow: ${DesignTokens.shadow.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    bottom: ${DesignTokens.spacing.lg};
    right: ${DesignTokens.spacing.lg};
    padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.lg};
    font-size: ${DesignTokens.typography.fontSize.sm};
  }
`;

// Airbnb-style Day Tile (keeping for reference, but using new DayCard above)
const DayTile = styled(motion.div)`
  background: white;
  border-radius: ${DesignTokens.borderRadius.xl};
  overflow: hidden;
  margin-bottom: ${DesignTokens.spacing['2xl']};
  box-shadow: ${DesignTokens.shadow.md};
  border: 1px solid ${props => props.theme.colors.borderLight};
  transition: all ${DesignTokens.transition.normal};

  &:hover {
    box-shadow: ${DesignTokens.shadow.xl};
    transform: translateY(-2px);
  }
`;

const DayTileHeader = styled.div`
  position: relative;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary || '#4285F4'} 0%, ${props => props.theme.colors.secondary || '#34A853'} 100%);
  padding: ${DesignTokens.spacing['2xl']};
  color: white;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.xl};
    flex-direction: column;
    text-align: center;
  }
`;

const DayNumberCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  flex-shrink: 0;
  color: white;
`;

const DayHeaderContent = styled.div`
  flex: 1;
`;

const DayTitle = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  margin: 0 0 ${DesignTokens.spacing.xs} 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.xl};
  }
`;

const DaySubtitle = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: ${DesignTokens.typography.fontWeight.medium};
`;

const DayContent = styled.div`
  padding: ${DesignTokens.spacing['2xl']};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.xl};
  }
`;

const ActivitySection = styled.div`
  margin-bottom: ${DesignTokens.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ActivitySectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  margin-bottom: ${DesignTokens.spacing.lg};
  padding-bottom: ${DesignTokens.spacing.md};
  border-bottom: 2px solid ${props => props.theme.colors.borderLight};
`;

const ActivitySectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${DesignTokens.borderRadius.md};
  background: ${props => props.iconBg || props.theme.colors.primaryLight || '#E8F0FE'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor || props.theme.colors.primary || '#4285F4'};
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ActivitySectionTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  text-transform: capitalize;
`;

const ActivityItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${DesignTokens.spacing.md};
  margin-bottom: ${DesignTokens.spacing.sm};
`;

const ActivityImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${DesignTokens.borderRadius.md};
  background-image: url(${props => optimizeBackgroundImage(props.imageUrl || 'https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b', { width: 400, height: 400, quality: 85 })});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  box-shadow: ${DesignTokens.shadow.sm};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 80px;
    height: 80px;
  }
`;

const ActivityItemContent = styled.div`
  flex: 1;
`;

const ActivityDescription = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
`;

const ActivityMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textTertiary};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PlaceTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: ${props => props.theme.colors.primaryLight || '#E8F0FE'};
  color: ${props => props.theme.colors.primary || '#4285F4'};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  margin-top: ${DesignTokens.spacing.xs};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

// Legacy DayCard (keeping for backward compatibility)
const LegacyDayCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${DesignTokens.borderRadius.md};
  padding: ${DesignTokens.spacing.lg};
  margin-bottom: ${DesignTokens.spacing.lg};
  border: 1px solid ${props => props.theme.colors.borderLight};
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.md};
  margin-bottom: ${DesignTokens.spacing.lg};
`;

const DayNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary || '#4285F4'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${DesignTokens.typography.fontSize.lg};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  flex-shrink: 0;
`;

const TimeSection = styled.div`
  margin-bottom: ${DesignTokens.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const TimeSectionTitle = styled.h4`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${DesignTokens.spacing.md} 0;
  text-transform: uppercase;
  letter-spacing: ${DesignTokens.typography.letterSpacing.wide};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
`;

// Legacy ActivityCard (keeping for backward compatibility with old format)
const ActivityCard = styled.div`
  background: white;
  padding: ${DesignTokens.spacing.md};
  border-radius: ${DesignTokens.borderRadius.md};
  margin-bottom: ${DesignTokens.spacing.sm};
  border-left: 3px solid ${props => props.theme.colors.primary || '#4285F4'};
`;

// Legacy ActivityTitle (keeping for backward compatibility)
const LegacyActivityTitle = styled.div`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${DesignTokens.spacing.xs};
`;

// Legacy ActivityDescription (keeping for backward compatibility)
const LegacyActivityDescription = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  margin: 0 0 ${DesignTokens.spacing.xs} 0;
`;

// Legacy ActivityMeta (keeping for backward compatibility)
const LegacyActivityMeta = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.md};
  flex-wrap: wrap;
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: ${props => props.theme.colors.textTertiary};
  margin-top: ${DesignTokens.spacing.xs};
`;

const SpecialSection = styled.div`
  background: ${props => props.theme.colors.primaryLight || '#E8F0FE'};
  padding: ${DesignTokens.spacing.lg};
  border-radius: ${DesignTokens.borderRadius.md};
  margin-top: ${DesignTokens.spacing.lg};
  border-left: 4px solid ${props => props.theme.colors.primary || '#4285F4'};
`;

const SpecialTitle = styled.h4`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
`;

const SpecialText = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: ${props => props.theme.colors.text};
  margin: 0;
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${DesignTokens.spacing['3xl']} ${DesignTokens.spacing.xl};
  color: ${props => props.theme.colors.textSecondary};
  gap: ${DesignTokens.spacing.md};
`;

// Simple, Reliable Loader Component (CSS-based, works everywhere)
const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${DesignTokens.spacing['2xl']};
`;

const LoaderDialog = styled.div`
  background: white;
  border-radius: ${DesignTokens.borderRadius.xl};
  padding: ${DesignTokens.spacing['2xl']};
  box-shadow: ${DesignTokens.shadow.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  width: 100%;
  animation: fadeIn 0.3s ease-in;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LoaderSpinner = styled.div`
  width: 80px;
  height: 80px;
  position: relative;
  margin-bottom: ${DesignTokens.spacing.xl};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 4px solid ${props => props.theme.colors.primaryLight || '#E8F0FE'};
    border-top-color: ${props => props.theme.colors.primary || '#4285F4'};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  &::after {
    content: '✦';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 32px;
    color: ${props => props.theme.colors.primary || '#4285F4'};
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 0.7;
      transform: translate(-50%, -50%) scale(1.1);
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    width: 60px;
    height: 60px;
    
    &::after {
      font-size: 24px;
    }
  }
`;

const LoaderText = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.text || '#202124'};
  margin-top: ${DesignTokens.spacing.lg};
`;

const LoaderMainText = styled.div`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  margin-bottom: ${DesignTokens.spacing.sm};
  color: ${props => props.theme.colors.text || '#202124'};
`;

const LoaderSubText = styled.div`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  margin-top: ${DesignTokens.spacing.xs};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
`;

const ErrorState = styled.div`
  background: ${props => props.theme.colors.tertiaryLight || '#FCE8E6'};
  color: ${props => props.theme.colors.tertiary || '#EA4335'};
  padding: ${DesignTokens.spacing.lg};
  border-radius: ${DesignTokens.borderRadius.md};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  margin-top: ${DesignTokens.spacing.xl};
`;

// ==========================================
// Glass-morphism Form Components (Screen 1)
// ==========================================

// Sentence Builder - "I want to go to [dest] for [days] days with a [budget] budget"
const SentenceBuilder = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
  line-height: 2.2;

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const SentenceText = styled.span`
  white-space: nowrap;
`;

// Transparent inline input with bottom border (for sentence builder)
const SentenceInput = styled.input`
  background: transparent;
  border: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.5);
  color: white;
  font-size: inherit;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  min-width: ${props => props.$width || '80px'};
  max-width: 200px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-bottom-color: #4285F4;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  /* Remove number input spinners */
  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

// Transparent inline select with bottom border
const SentenceSelect = styled.select`
  background: transparent;
  border: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.5);
  color: white;
  font-size: inherit;
  font-weight: 600;
  padding: 0.25rem 1.5rem 0.25rem 0.5rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.25rem center;

  &:focus {
    border-bottom-color: #4285F4;
  }

  option {
    background: #1e293b;
    color: white;
  }
`;

// Travel Style Grid - 3 image cards
const TravelStyleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
`;

const TravelStyleCard = styled(motion.button)`
  position: relative;
  border: 2px solid ${props => props.$active
    ? 'rgba(66, 133, 244, 0.8)'
    : 'rgba(255, 255, 255, 0.2)'};
  border-radius: ${DesignTokens.borderRadius.md};
  overflow: hidden;
  cursor: pointer;
  background: none;
  padding: 0;
  aspect-ratio: 3 / 2;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(66, 133, 244, 0.6);
  }

  ${props => props.$active && `
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.4), ${GlassmorphismTokens.shadow.blueCTA};
  `}

  @media (max-width: 640px) {
    aspect-ratio: 1 / 1;
  }
`;

const TravelStyleImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${props => props.$src});
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%);
  }
`;

const TravelStyleLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.5rem;
  z-index: 1;

  .title {
    font-size: 0.8rem;
    font-weight: 600;
    color: white;
    margin-bottom: 0;
  }

  .desc {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.3;
  }

  @media (max-width: 640px) {
    padding: 0.375rem;

    .title {
      font-size: 0.7rem;
    }

    .desc {
      display: none;
    }
  }
`;

// Magic Box Textarea (glass-styled custom prompt)
const MagicBoxTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${DesignTokens.borderRadius.md};
  color: white;
  font-size: 0.9375rem;
  font-family: ${DesignTokens.typography.fontFamily.primary};
  resize: vertical;
  outline: none;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;

  &:focus {
    border-color: rgba(66, 133, 244, 0.6);
    background: rgba(255, 255, 255, 0.12);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

// Glass-themed field label for the form
const GlassFieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

// Glass-themed options toggle
const GlassOptionsToggle = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  justify-content: flex-start;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s ease;
    transform: rotate(${props => props.$isOpen ? '90deg' : '0deg'});
  }
`;

// Glass-themed advanced options grid
const GlassOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Glass-themed select for advanced options
const GlassSelect = styled.select`
  width: 100%;
  padding: 0.75rem 2rem 0.75rem 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${DesignTokens.borderRadius.md};
  color: white;
  font-size: 0.9375rem;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    border-color: rgba(66, 133, 244, 0.6);
    background: rgba(255, 255, 255, 0.12);
  }

  option {
    background: #1e293b;
    color: white;
  }
`;

// Glass-themed text input for advanced options
const GlassInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${DesignTokens.borderRadius.md};
  color: white;
  font-size: 0.9375rem;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: rgba(66, 133, 244, 0.6);
    background: rgba(255, 255, 255, 0.12);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

// Export all styled components
export {
  PageContainer,
  LayoutContainer,
  Sidebar,
  CompassIconContainer,
  ChatContainer,
  AIPromptBubble,
  UserBubble,
  VoiceControls,
  MicrophoneButton,
  VolumeSlider,
  MainContent,
  MainHeader,
  MainTitle,
  SearchIconButton,
  ContentArea,
  SectionTitle,
  Container,
  Title,
  Header,
  BackButton,
  FormContainer,
  ExpandedHeader,
  ExpandedTitle,
  PrimaryFieldsRow,
  FieldGroup,
  FieldLabelModern,
  FieldInputModern,
  FieldSelectModern,
  OptionsToggle,
  OptionsGrid,
  GenerateButton,
  ItineraryContainer,
  ItineraryHeader,
  ItineraryTitle,
  ActionButtons,
  ActionButton,
  TripSummary,
  TripSummaryText,
  DayCardsGrid,
  DayCard,
  DayHeaderBanner,
  DayHeaderIcon,
  DayHeaderTitle,
  DayCardContent,
  DayActivities,
  ActivityTimeBlock,
  ActivityTimeLabel,
  ActivityText,
  ActivityItem,
  ActivityTitleRow,
  ActivityTitle,
  ActivityDuration,
  ActivityGlimpse,
  ActivityNextUp,
  ActivityMetaRow,
  CostBadge,
  TransitBadge,
  TransitDetailsSection,
  WhyFunSection,
  SourceAttribution,
  DailyNotesCard,
  DayTipBanner,
  TipsSection,
  TipsSectionTitle,
  TipsList,
  TipItem,
  SpecialRecommendationsSection,
  SpecialRecommendationsTitle,
  SpecialRecommendationsSubtitle,
  RecommendationsGrid,
  RecommendationCard,
  RecommendationHeader,
  RecommendationName,
  RecommendationPlaceType,
  RecommendationContent,
  RecommendationWhyFamous,
  RecommendationDetails,
  RecommendationDetailRow,
  RecommendationDetailLabel,
  RecommendationDetailValue,
  RecommendationInsiderTip,
  AccommodationSection,
  AccommodationTitle,
  AccommodationText,
  AccommodationLink,
  BrowseAllButton,
  ToggleViewButton,
  AvailableItinerariesContainer,
  SectionHeader,
  SectionSubtitle,
  ItineraryTypeBadge,
  ItineraryCardsContainer,
  ItineraryCarouselContainer,
  ItineraryCarouselCardsContainer,
  ItineraryCarouselCard,
  CarouselButton,
  CarouselPrevButton,
  CarouselNextButton,
  CountryCard,
  CountrySection,
  CountrySectionHeader,
  CountryTitle,
  CountryCount,
  ItineraryCard,
  ItineraryImage,
  AIBadgeSmall,
  ItineraryContent,
  ItineraryCardTitle,
  ItineraryCardDescription,
  ItineraryCardMeta,
  FavoriteButton,
  BudgetTrackerSection,
  BudgetTrackerHeader,
  BudgetTrackerTitle,
  EstimatedCost,
  CostAmount,
  BudgetNoteInput,
  CostSlider,
  ShareButton,
  DayTile,
  DayTileHeader,
  DayNumberCircle,
  DayHeaderContent,
  DayTitle,
  DaySubtitle,
  DayContent,
  ActivitySection,
  ActivitySectionHeader,
  ActivitySectionIcon,
  ActivitySectionTitle,
  ActivityItemHeader,
  ActivityImage,
  ActivityItemContent,
  ActivityDescription,
  ActivityMeta,
  MetaItem,
  PlaceTag,
  LegacyDayCard,
  DayHeader,
  DayNumber,
  TimeSection,
  TimeSectionTitle,
  ActivityCard,
  LegacyActivityTitle,
  LegacyActivityDescription,
  LegacyActivityMeta,
  SpecialSection,
  SpecialTitle,
  SpecialText,
  LoadingState,
  LoaderContainer,
  LoaderDialog,
  LoaderSpinner,
  LoaderText,
  LoaderMainText,
  LoaderSubText,
  ErrorState,
  ScenicFormBackground,
  SentenceBuilder,
  SentenceText,
  SentenceInput,
  SentenceSelect,
  TravelStyleGrid,
  TravelStyleCard,
  TravelStyleImage,
  TravelStyleLabel,
  MagicBoxTextarea,
  GlassFieldLabel,
  GlassOptionsToggle,
  GlassOptionsGrid,
  GlassSelect,
  GlassInput
};
